'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useFetchSensorDataMutation, type GraphDefinition, type SearchParams } from '@/store/api/dataLayersApi';
import {
  normalizeMetadataThreshold,
  transformMetadataThresholdToChartFormat,
  mergeThresholds,
} from '@/modules/shared/DataLayers/utils/transform-metadata-thresholds';

// Type definitions
export type SensorActionType =
  | 'initial' // Initial load when component mounts
  | 'enable' // Adding/enabling a sensor
  | 'disable' // Disabling a sensor
  | 'filter' // Filter change
  | 'sensor-change' // Sensor state change (enable/disable)
  | 'reset' // Reset section to default state
  | 'refetch' // Manual refetch
  | 'force-reload'; // Force reload all data

interface SensorDataState {
  data: Record<string, any>;
  loading: Record<string, boolean>; // Section-level loading
  sensorLoading: Record<string, Record<string, boolean>>; // Individual sensor loading per section
  error: string | null;
}

// Extended GraphDefinition to include runtime properties
interface ExtendedGraphDefinition extends GraphDefinition {
  activeFeatureIds?: string[];
  filters?: any;
}

export const useFetchData = ({
  queryParams,
  graphDefinitions,
  activeFeatures,
  activeDataLayerFilters,
  resetTrigger = false,
  currentEnabledSensors,
  onLoadingStateChange,
  externalLegendStates,
  expandableLegendForApi,
  focusedSensors,
}: {
  queryParams: SearchParams;
  graphDefinitions: GraphDefinition[];
  activeFeatures?: Record<string, Record<string, boolean>>;
  activeDataLayerFilters?: any;
  resetTrigger?: boolean;
  currentEnabledSensors?: Record<string, Record<string, boolean>>;
  onLoadingStateChange?: (isLoading: boolean) => void;
  externalLegendStates?: Record<string, boolean>;
  expandableLegendForApi?: any; // Expandable legend to include in API body (not affecting signature)
  focusedSensors?: Record<string, string>; // Map of sectionId -> focused sensor name (for caching)
}) => {
  // Cache key for sessionStorage
  const cacheKeyPrefix = 'sensorData_cache_';

  // Helper to load cached data
  const loadCachedData = useCallback(() => {
    try {
      const cacheKey = `${cacheKeyPrefix}${queryParams?.site_id}_${queryParams?.asset_id}_${queryParams?.component_id}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still fresh (< 5 minutes old)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          return parsed.data || {};
        }
      }
    } catch (error) {}
    return {};
  }, [queryParams?.site_id, queryParams?.asset_id, queryParams?.component_id]);

  // Enhanced state object with individual sensor loading - initialize with cached data
  const [state, setState] = useState<SensorDataState>(() => {
    const cachedData = loadCachedData();
    return {
      data: cachedData,
      loading: {},
      sensorLoading: {},
      error: null,
    };
  });

  // Single source of truth for abort controllers
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const [fetchSensorData] = useFetchSensorDataMutation();

  // Cache for focused sensor data: key = `${sectionId}_${focusedSensor}`, value = response data
  // This allows returning to a previously focused sensor without refetching
  const focusedSensorCacheRef = useRef<Record<string, any>>({});

  // Track previous focused sensors to detect changes
  const prevFocusedSensorsRef = useRef<Record<string, string>>({});

  // Create stable key for state.data that only changes when section keys change
  // This prevents infinite loops while still detecting meaningful data changes
  const stableDataKey = useMemo(() => {
    const keys = Object.keys(state.data).sort().join(',');
    const lengths = Object.entries(state.data)
      .map(([k, v]) => `${k}:${v?.data?.length || 0}`)
      .join(',');
    return `${keys}|${lengths}`;
  }, [state.data]);

  // Effect to handle focus changes - save to cache when leaving, restore from cache when returning
  useEffect(() => {
    if (!focusedSensors) return;

    const prevFocused = prevFocusedSensorsRef.current;

    // For each section, check if focus changed
    Object.entries(focusedSensors).forEach(([sectionId, currentSensor]) => {
      const prevSensor = prevFocused[sectionId];

      // If focus changed to a different sensor
      if (prevSensor && prevSensor !== currentSensor) {
        // Save current data under the OLD sensor's cache key (before it gets overwritten)
        const prevCacheKey = `${sectionId}_${prevSensor}`;
        if (state.data[sectionId]) {
          focusedSensorCacheRef.current[prevCacheKey] = state.data[sectionId];
        }
      }

      // Check if we have cached data for the new sensor
      if (currentSensor) {
        const newCacheKey = `${sectionId}_${currentSensor}`;
        const cachedData = focusedSensorCacheRef.current[newCacheKey];

        if (cachedData && prevSensor !== currentSensor) {
          // Restore cached data for this sensor
          setState((prev) => ({
            ...prev,
            data: { ...prev.data, [sectionId]: cachedData },
          }));
        }
      }
    });

    // Also handle sensors that were focused before but are no longer in focusedSensors
    Object.entries(prevFocused).forEach(([sectionId, prevSensor]) => {
      if (prevSensor && !focusedSensors[sectionId]) {
        // Save data for sensor that is no longer focused
        const cacheKey = `${sectionId}_${prevSensor}`;
        if (state.data[sectionId]) {
          focusedSensorCacheRef.current[cacheKey] = state.data[sectionId];
        }
      }
    });

    // Update previous ref
    prevFocusedSensorsRef.current = { ...focusedSensors };
  }, [focusedSensors, stableDataKey]); // Use stableDataKey instead of state.data

  // Save data to cache whenever it changes
  useEffect(() => {
    if (Object.keys(state.data).length > 0) {
      try {
        const cacheKey = `${cacheKeyPrefix}${queryParams?.site_id}_${queryParams?.asset_id}_${queryParams?.component_id}`;
        const cacheData = {
          data: state.data,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (error) {}
    }
  }, [state.data, queryParams?.site_id, queryParams?.asset_id, queryParams?.component_id]);

  // Create stable query signature to prevent unnecessary requests
  const querySignature = useMemo(() => {
    if (!queryParams?.site_id || !queryParams?.asset_id) return '';

    // For alarms, only track sensors that DON'T have data yet
    // This allows refetching when NEW alarms are added (not in current data)
    // but prevents refetches when just toggling alarms we already have data for
    const isAlarmDataLayer = queryParams?.data_layer?.includes('alarm');

    let enabledSensorsForSignature;
    if (isAlarmDataLayer && currentEnabledSensors) {
      // For alarms: Track ALL enabled sensors to detect when enabled list changes
      // Since alarms fetch all data with enabled=true in API, we just need to detect
      // when the user toggles sensors on/off to trigger appropriate refetches


      // Track all enabled sensors, organized by section
      // Keep the structure as Record<string, Record<string, boolean>> to match comparison logic
      const allEnabledSensors: Record<string, Record<string, boolean>> = {};
      Object.entries(currentEnabledSensors).forEach(([key, value]) => {
        // Check if this is a nested structure (sectionId -> sensors map)
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const sectionId = key;
          const sensors = value as Record<string, boolean>;

          // Get all enabled sensors for this section (keep as boolean map)
          const enabledSensorsMap: Record<string, boolean> = {};
          Object.entries(sensors).forEach(([sensorName, enabled]) => {
            if (enabled) {
              enabledSensorsMap[sensorName] = true;
            }
          });

          if (Object.keys(enabledSensorsMap).length > 0) {
            allEnabledSensors[sectionId] = enabledSensorsMap;
          }
        }
      });
      enabledSensorsForSignature = allEnabledSensors;
    } else {
      // For other data layers: Track full enabled sensor state
      enabledSensorsForSignature = currentEnabledSensors || {};
    }

    const signature = JSON.stringify({
      queryParams: {
        site_id: queryParams.site_id,
        asset_id: queryParams.asset_id,
        component_id: queryParams.component_id,
        // subcomponent_id: queryParams.subcomponent_id, // Not in SearchParams type
        from_date: queryParams.from_date,
        to_date: queryParams.to_date,
        asset_category: queryParams.asset_category,
        full_pull: queryParams.full_pull,
      },
      enabledSensors: enabledSensorsForSignature,
      activeFeatures: activeFeatures || {},
      activeDataLayerFilters: activeDataLayerFilters || [],
      graphDefinitions: graphDefinitions.map((g: any) => ({
        id: g.id,
        enabled: g.enabled,
        legendCount: g.legend?.length || 0,
      })),
      resetTrigger,
    });

    return signature;
  }, [
    queryParams?.site_id,
    queryParams?.asset_id,
    queryParams?.component_id,
    // queryParams?.subcomponent_id, // Not in SearchParams type
    queryParams?.from_date,
    queryParams?.to_date,
    queryParams?.asset_category,
    queryParams?.full_pull,
    queryParams?.data_layer,
    currentEnabledSensors,
    activeFeatures,
    activeDataLayerFilters,
    graphDefinitions,
    resetTrigger,
    stableDataKey, // For alarm sensor data checking
  ]);

  const lastQuerySignatureRef = useRef<string>('');

  // Track global loading state and communicate to parent
  const isAnySectionLoading = useMemo(() => {
    return (
      Object.values(state.loading).some(Boolean) ||
      Object.values(state.sensorLoading).some((sensorMap) => Object.values(sensorMap).some(Boolean))
    );
  }, [state.loading, state.sensorLoading]);

  // Communicate loading state changes to parent
  useEffect(() => {
    onLoadingStateChange?.(isAnySectionLoading);
  }, [isAnySectionLoading, onLoadingStateChange]);

  // Individual sensor loading state management
  const setSensorLoading = useCallback((sectionId: string, sensorName: string, isLoading: boolean) => {
    setState((prev) => ({
      ...prev,
      sensorLoading: {
        ...prev.sensorLoading,
        [sectionId]: {
          ...prev.sensorLoading[sectionId],
          [sensorName]: isLoading,
        },
      },
    }));
  }, []);

  const isSensorLoading = useCallback(
    (sectionId: string, sensorName: string): boolean => {
      return state.sensorLoading[sectionId]?.[sensorName] || false;
    },
    [state.sensorLoading]
  );

  // Check if any sensor in a section is loading
  const isAnySensorLoadingInSection = useCallback(
    (sectionId: string): boolean => {
      return Object.values(state.sensorLoading[sectionId] || {}).some(Boolean);
    },
    [state.sensorLoading]
  );

  // Simple data validation
  const validateSensorData = useCallback((response: any): boolean => {
    if (!response?.data || !Array.isArray(response.data)) return false;
    return response.data.length > 0;
  }, []);

  // Apply features and enabled sensors to graph definition
  const applyFeaturesToGraphDef = useCallback(
    (
      graphDef: GraphDefinition,
      sectionFeatures?: Record<string, boolean>,
      sectionEnabledSensors?: Record<string, boolean>
    ): ExtendedGraphDefinition => {
      const result: ExtendedGraphDefinition = { ...graphDef };

      // IMPORTANT: For alarms, do NOT apply externalLegendStates to the API request
      // Legend states should only control visual rendering, not data fetching
      // This prevents "No data available" when toggling alarm legends
      const isAlarmDataLayer = queryParams?.data_layer?.includes('alarm');

      // Apply external legend states if available, otherwise use sensor states
      if (result.legend) {
        result.legend = result.legend.map((legend: any) => ({
          ...legend,
          // Use sensor enabled states - default to false if not explicitly enabled
          // For alarms: always keep enabled=true in API request to fetch all data
          enabled: isAlarmDataLayer
            ? true
            : externalLegendStates?.[legend.id] !== undefined
            ? externalLegendStates[legend.id]
            : sectionEnabledSensors?.[legend.displayName] === true,
        }));
      }

      // Apply feature filters - with proper null/undefined checking
      if (sectionFeatures && result.features && Array.isArray(result.features) && result.features.length > 0) {
        const activeFeatures = result.features.filter(
          (feature: any) => feature?.id && sectionFeatures[feature.id] === true
        );
        if (activeFeatures.length > 0) {
          result.features = activeFeatures;
          result.activeFeatureIds = activeFeatures.map((f: any) => f.id).filter(Boolean);
        }
      }

      return result;
    },
    [externalLegendStates, queryParams?.data_layer]
  );

  // Fetch data for a specific sensor in a section
  const fetchSensorData_Individual = useCallback(
    async (sectionId: string, sensorName: string, actionType: SensorActionType = 'enable') => {
      const graphDef = graphDefinitions.find((g) => g.id === sectionId);
      if (!graphDef || !queryParams?.site_id || !queryParams?.asset_id) {
        return;
      }

      // Set loading ONLY for this specific section
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, [sectionId]: true },
      }));

      try {
        // Apply current filters and enabled sensors
        const sectionFeatures = activeFeatures?.[sectionId];
        const sectionEnabledSensors = currentEnabledSensors?.[sectionId];

        const filteredGraphDef = {
          ...applyFeaturesToGraphDef(graphDef, sectionFeatures, sectionEnabledSensors),
          filters: activeDataLayerFilters,
          ...(expandableLegendForApi && { expandableLegend: expandableLegendForApi }),
        };

        const response = await fetchSensorData({
          params: queryParams,
          body: filteredGraphDef,
        }).unwrap();

        // Transform alarm data if needed (from names/values format to sensor format)
        let transformedResponse = response;

        // Add color information to sensor data if not present
        if (
          transformedResponse?.data &&
          Array.isArray(transformedResponse.data) &&
          !queryParams.data_layer?.includes('alarm')
        ) {
          // Get the section's configured colors
          const sectionColors = graphDef.colors || [];
          const sensorNames = graphDef.legend?.map((l: any) => l.displayName) || [];

          transformedResponse = {
            ...transformedResponse,
            data: transformedResponse.data.map((trace: any) => {
              const sensorIndex = sensorNames.indexOf(trace.displayName || trace.name);
              const color = sensorIndex >= 0 ? sectionColors[sensorIndex] : undefined;
              return color ? { ...trace, color } : trace;
            }),
          };
        }

        // Transform alarm data from backend format (names/values arrays) to sensor format
        const isAlarmData = queryParams.data_layer === 'alarm';
        const alarmDataArray = isAlarmData && response?.data ? response.data : (Array.isArray(response) ? response : null);

        if (isAlarmData && Array.isArray(alarmDataArray) && alarmDataArray.length > 0 && alarmDataArray[0]?.date) {
          // Inline transformation to avoid reference errors
          const uniqueAlarmNames = new Set<string>();
          const alarmDataByName: Record<string, { x: string[]; y: number[]; tooltips: any[] }> = {};

          alarmDataArray.forEach((entry: any) => {
            if (entry.names && Array.isArray(entry.names)) {
              entry.names.forEach((name: string, idx: number) => {
                uniqueAlarmNames.add(name);

                if (!alarmDataByName[name]) {
                  alarmDataByName[name] = { x: [], y: [], tooltips: [] };
                }

                alarmDataByName[name].x.push(entry.date);
                alarmDataByName[name].y.push(entry.values?.[idx] || 0);
                // Store tooltip data for this specific alarm at this date
                alarmDataByName[name].tooltips.push(entry.tooltip?.[name] || {});
              });
            }
          });

          // Create data array in sensor format WITH tooltip data and colors
          const data = Array.from(uniqueAlarmNames).map((name, index) => {
            // Get color from graph config if available
            let color: string | undefined = undefined;
            if (graphDef.legend && graphDef.colors) {
              const legendIndex = graphDef.legend.findIndex(
                (item: any) => item.displayName === name || item.name === name
              );
              if (legendIndex >= 0) {
                color = graphDef.colors[legendIndex];
              }
            }

            return {
              displayName: name,
              name: name,
              x: alarmDataByName[name].x,
              y: alarmDataByName[name].y,
              type: 'bar', // Important: set type to bar for alarms
              alarmTooltips: alarmDataByName[name].tooltips, // Store tooltip data
              isAlarm: true, // Mark as alarm data
              color: color, // Add color from config
            };
          });

          // Create legend array
          const legend = Array.from(uniqueAlarmNames).map((name) => ({
            displayName: name,
            enabled: true,
          }));

          // Preserve start/end datetime from original response, or calculate from dates
          const start_datetime = response.start_datetime || alarmDataArray[0]?.date || new Date().toISOString();
          const end_datetime = response.end_datetime || alarmDataArray[alarmDataArray.length - 1]?.date || new Date().toISOString();

          transformedResponse = {
            data,
            legend,
            sensorColors: {}, // Alarm colors will be assigned by PlotlyChart
            start_datetime,
            end_datetime,
          };

        }

        // Process metadata thresholds and add them to sensor data
        // This handles the new backend feature where thresholds can come from metadata
        // instead of (or in addition to) the sensor data response
        if (transformedResponse?.data && Array.isArray(transformedResponse.data) && graphDef.legend) {
          transformedResponse = {
            ...transformedResponse,
            data: transformedResponse.data.map((sensorData: any) => {
              // Find the matching legend item for this sensor to get its metadata
              const legendItem = graphDef.legend.find(
                (item: any) => item.displayName === sensorData.displayName || item.displayName === sensorData.name
              );

              // Check if this sensor has ui_threshold defined in metadata
              if (legendItem?.ui_threshold) {
                // Step 1: Normalize the threshold value
                // Backend can send either:
                // - Legacy format: single number (e.g., 100.0)
                // - New format: array of threshold objects [{id, value, label, color}, ...]
                const normalizedThresholds = normalizeMetadataThreshold(legendItem.ui_threshold);

                // Step 2: Transform metadata format to chart-compatible format
                // Adds required fields: sensorName, visible, dash
                // Provides defaults for optional fields: color, label
                const metadataThresholds = transformMetadataThresholdToChartFormat(
                  sensorData.displayName || sensorData.name,
                  normalizedThresholds
                );

                // Step 3: Merge with existing thresholds
                // Data API thresholds take precedence (if they exist)
                // Metadata thresholds are used as fallback
                const mergedThresholds = mergeThresholds(sensorData.thresholds, metadataThresholds);

                return {
                  ...sensorData,
                  thresholds: mergedThresholds,
                };
              }

              return sensorData;
            }),
          };
        }

        const isValid = validateSensorData(transformedResponse);

        setState((prev) => ({
          ...prev,
          data: { ...prev.data, [sectionId]: transformedResponse },
          loading: { ...prev.loading, [sectionId]: false },
        }));
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, [sectionId]: false },
          error: `Failed to load ${sensorName}: ${error.message}`,
        }));
      }
    },
    [
      graphDefinitions,
      queryParams,
      activeFeatures,
      activeDataLayerFilters,
      currentEnabledSensors,
      fetchSensorData,
      applyFeaturesToGraphDef,
      validateSensorData,
    ]
  );

  // Original section fetch function (for initial loads and resets)
  const fetchSectionData = useCallback(
    async (sectionId: string, actionType: SensorActionType = 'initial') => {
      const graphDef = graphDefinitions.find((g) => g.id === sectionId);
      if (!graphDef || !queryParams?.site_id || !queryParams?.asset_id) {
        return;
      }

      // Cancel any existing request for this section
      if (abortControllersRef.current[sectionId]) {
        abortControllersRef.current[sectionId].abort();
        delete abortControllersRef.current[sectionId];
      }

      // Create new abort controller
      const abortController = new AbortController();
      abortControllersRef.current[sectionId] = abortController;

      // Set loading ONLY for this specific section
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, [sectionId]: true },
        error: null,
      }));

      try {
        // Apply current filters and enabled sensors
        const sectionFeatures = activeFeatures?.[sectionId];
        const sectionEnabledSensors = currentEnabledSensors?.[sectionId];

        const filteredGraphDef = {
          ...applyFeaturesToGraphDef(graphDef, sectionFeatures, sectionEnabledSensors),
          filters: activeDataLayerFilters,
          ...(expandableLegendForApi && { expandableLegend: expandableLegendForApi }),
        };

        const startTime = performance.now();
        const response = await fetchSensorData({
          params: queryParams,
          body: filteredGraphDef,
          signal: abortController.signal,
        }).unwrap();
        const endTime = performance.now();

        // Only update if this request wasn't aborted
        if (!abortController.signal.aborted) {
          // Transform alarm data if needed (from names/values format to sensor format)
          let transformedResponse = response;

          // Add color information to sensor data if not present
          if (
            transformedResponse?.data &&
            Array.isArray(transformedResponse.data) &&
            !queryParams.data_layer?.includes('alarm')
          ) {
            // Get the section's configured colors
            const sectionColors = graphDef.colors || [];
            const sensorNames = graphDef.legend?.map((l: any) => l.displayName) || [];

            transformedResponse = {
              ...transformedResponse,
              data: transformedResponse.data.map((trace: any) => {
                const sensorIndex = sensorNames.indexOf(trace.displayName || trace.name);
                const color = sensorIndex >= 0 ? sectionColors[sensorIndex] : undefined;
                return color ? { ...trace, color } : trace;
              }),
            };
          }
          // Transform alarm data from backend format (names/values arrays) to sensor format
          const isAlarmData = queryParams.data_layer === 'alarm';
          const alarmDataArray = isAlarmData && response?.data ? response.data : (Array.isArray(response) ? response : null);

          if (isAlarmData && Array.isArray(alarmDataArray) && alarmDataArray.length > 0 && alarmDataArray[0]?.date) {
            // Inline transformation to avoid reference errors
            const uniqueAlarmNames = new Set<string>();
            const alarmDataByName: Record<string, { x: string[]; y: number[]; tooltips: any[] }> = {};

            alarmDataArray.forEach((entry: any) => {
              if (entry.names && Array.isArray(entry.names)) {
                entry.names.forEach((name: string, idx: number) => {
                  uniqueAlarmNames.add(name);

                  if (!alarmDataByName[name]) {
                    alarmDataByName[name] = { x: [], y: [], tooltips: [] };
                  }

                  alarmDataByName[name].x.push(entry.date);
                  alarmDataByName[name].y.push(entry.values?.[idx] || 0);
                  // Store tooltip data for this specific alarm at this date
                  alarmDataByName[name].tooltips.push(entry.tooltip?.[name] || {});
                });
              }
            });

            // Create data array in sensor format WITH tooltip data and colors
            const data = Array.from(uniqueAlarmNames).map((name, index) => {
              // Get color from graph config if available
              let color: string | undefined = undefined;
              if (graphDef.legend && graphDef.colors) {
                const legendIndex = graphDef.legend.findIndex(
                  (item: any) => item.displayName === name || item.name === name
                );
                if (legendIndex >= 0) {
                  color = graphDef.colors[legendIndex];
                }
              }

              return {
                displayName: name,
                name: name,
                x: alarmDataByName[name].x,
                y: alarmDataByName[name].y,
                type: 'bar', // Important: set type to bar for alarms
                alarmTooltips: alarmDataByName[name].tooltips, // Store tooltip data
                isAlarm: true, // Mark as alarm data
                color: color, // Add color from config
              };
            });

            // Create legend array - all alarms should be enabled by default
            const legend = Array.from(uniqueAlarmNames).map((name) => ({
              displayName: name,
              enabled: true,
            }));

            // Don't override sensorColors here - let the container handle color mapping
            // This allows the colors to come from the legend config
            const sensorColors: Record<string, string> = {};

            // Preserve start/end datetime from original response, or calculate from dates
            const start_datetime = response.start_datetime || alarmDataArray[0]?.date || new Date().toISOString();
            const end_datetime = response.end_datetime || alarmDataArray[alarmDataArray.length - 1]?.date || new Date().toISOString();

            transformedResponse = {
              data,
              legend,
              sensorColors,
              start_datetime,
              end_datetime,
            };

          }

          // Process metadata thresholds and add them to sensor data
          // Same processing as individual fetch - see detailed comments above
          if (transformedResponse?.data && Array.isArray(transformedResponse.data) && graphDef.legend) {
            transformedResponse = {
              ...transformedResponse,
              data: transformedResponse.data.map((sensorData: any) => {
                // Find the matching legend item for this sensor
                const legendItem = graphDef.legend.find(
                  (item: any) => item.displayName === sensorData.displayName || item.displayName === sensorData.name
                );

                if (legendItem?.ui_threshold) {
                  // Normalize: handle both legacy number and new array formats
                  const normalizedThresholds = normalizeMetadataThreshold(legendItem.ui_threshold);

                  // Transform: add required chart fields and defaults
                  const metadataThresholds = transformMetadataThresholdToChartFormat(
                    sensorData.displayName || sensorData.name,
                    normalizedThresholds
                  );

                  // Merge: data API thresholds take precedence over metadata
                  const mergedThresholds = mergeThresholds(sensorData.thresholds, metadataThresholds);

                  return {
                    ...sensorData,
                    thresholds: mergedThresholds,
                  };
                }

                return sensorData;
              }),
            };
          }

          const isValid = validateSensorData(transformedResponse);

          // Debug: trace waveform data storage
          if (sectionId.toLowerCase().includes('waveform')) {
            // Debug log removed for performance
          }

          // FIXED: Use functional update to ensure we have the latest state
          setState((prev) => ({
            ...prev,
            data: { ...prev.data, [sectionId]: transformedResponse },
            loading: { ...prev.loading, [sectionId]: false },
            sensorLoading: { ...prev.sensorLoading, [sectionId]: {} }, // Clear all sensor loading states for this section
          }));
        }
      } catch (error: any) {
        if (error.name !== 'AbortError' && !abortController.signal.aborted) {
          setState((prev) => ({
            ...prev,
            loading: { ...prev.loading, [sectionId]: false },
            sensorLoading: { ...prev.sensorLoading, [sectionId]: {} }, // Clear sensor loading on error
            error: `Failed to load ${sectionId}: ${error.message}`,
          }));
        }
      } finally {
        // Clean up abort controller
        if (abortControllersRef.current[sectionId] === abortController) {
          delete abortControllersRef.current[sectionId];
        }
      }
    },
    [
      graphDefinitions,
      queryParams,
      activeFeatures,
      activeDataLayerFilters,
      currentEnabledSensors,
      fetchSensorData,
      applyFeaturesToGraphDef,
      validateSensorData,
      currentEnabledSensors,
      fetchSensorData,
      applyFeaturesToGraphDef,
      validateSensorData,
    ]
  );

  // Cancel all requests for a section
  const cancelSectionRequest = useCallback((sectionId: string) => {
    if (abortControllersRef.current[sectionId]) {
      abortControllersRef.current[sectionId].abort();
      delete abortControllersRef.current[sectionId];
    }

    setState((prev) => ({
      ...prev,
      loading: { ...prev.loading, [sectionId]: false },
      sensorLoading: { ...prev.sensorLoading, [sectionId]: {} },
    }));
  }, []);

  // Cancel all requests
  const cancelAllRequests = useCallback(() => {
    Object.values(abortControllersRef.current).forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current = {};

    setState((prev) => ({
      ...prev,
      loading: {},
      sensorLoading: {},
    }));
  }, []);

  // FIXED MAIN EFFECT - Simplified logic to handle filter changes immediately
  useEffect(() => {
    if (!querySignature || querySignature === lastQuerySignatureRef.current) {
      return;
    }

    const prevSignature = lastQuerySignatureRef.current;
    const currentSignature = querySignature;
    lastQuerySignatureRef.current = currentSignature;

    // If this is the first load, load everything
    if (!prevSignature) {
      cancelAllRequests();

      // Set loading state for all sections immediately on initial load
      const loadingState: Record<string, boolean> = {};
      graphDefinitions.forEach((g: any) => {
        if (g.enabled !== false) {
          loadingState[g.id] = true;
        }
      });

      if (resetTrigger) {
        setState((prev) => ({ ...prev, data: {}, error: null, loading: loadingState }));
      } else {
        setState((prev) => ({ ...prev, loading: loadingState }));
      }

      // Use requestAnimationFrame to ensure state is settled
      requestAnimationFrame(() => {
        const enabledSections = graphDefinitions.filter((g: any) => g.enabled !== false);
        enabledSections.forEach((graphDef: any) => {
          fetchSectionData(graphDef.id, 'initial');
        });
      });

      return;
    }

    // Parse signatures to detect what changed
    const prevData = JSON.parse(prevSignature);
    const currentData = JSON.parse(currentSignature);

    const queryParamsChanged = JSON.stringify(prevData.queryParams) !== JSON.stringify(currentData.queryParams);
    const resetTriggered = currentData.resetTrigger !== prevData.resetTrigger;
    const dataLayerFiltersChanged =
      JSON.stringify(prevData.activeDataLayerFilters) !== JSON.stringify(currentData.activeDataLayerFilters);

    // If query params or reset changed, reload everything
    if (queryParamsChanged || resetTriggered || dataLayerFiltersChanged) {
      cancelAllRequests();

      // Clear data immediately when component/part changes to avoid showing stale data
      const componentOrPartChanged =
        prevData.queryParams?.component_id !== currentData.queryParams?.component_id ||
        prevData.queryParams?.subcomponent_id !== currentData.queryParams?.subcomponent_id;

      // Set loading state for all sections immediately
      const loadingState: Record<string, boolean> = {};
      graphDefinitions.forEach((g: any) => {
        if (g.enabled !== false) {
          loadingState[g.id] = true;
        }
      });

      if (resetTriggered || componentOrPartChanged) {
        // Clear data and set loading state immediately
        setState((prev) => ({
          ...prev,
          data: {},
          error: null,
          loading: loadingState,
        }));
      } else {
        // Just set loading state without clearing data for other changes
        setState((prev) => ({
          ...prev,
          loading: loadingState,
          error: null,
        }));
      }

      // Use requestAnimationFrame to ensure state changes are processed
      requestAnimationFrame(() => {
        const enabledSections = graphDefinitions.filter((g: any) => g.enabled !== false);
        enabledSections.forEach((graphDef: any) => {
          fetchSectionData(graphDef.id, 'initial');
        });
      });

      return;
    }

    // Check for sensor changes per section
    const prevSensors = prevData.enabledSensors || {};
    const currentSensors = currentData.enabledSensors || {};
    const prevFeatures = prevData.activeFeatures || {};
    const currentFeatures = currentData.activeFeatures || {};

    const changedSections = new Set<string>();

    // Check sensor changes - only refetch if NEW sensors are being enabled
    // AND those sensors actually exist in the section's graph definition
    Object.keys({ ...prevSensors, ...currentSensors }).forEach((sectionId) => {
      const prevSectionSensors = prevSensors[sectionId] || {};
      const currentSectionSensors = currentSensors[sectionId] || {};

      // Get the graph definition for this section to check which sensors actually belong to it
      const graphDef = graphDefinitions.find((g) => g.id === sectionId);
      const sectionSensorNames = graphDef?.legend?.map((l: any) => l.displayName || l.name) || [];

      // Check if any sensors are newly enabled (were false/undefined, now true)
      // AND actually exist in this section
      let hasNewlyEnabledSensors = false;
      const newlyEnabledSensors: string[] = [];
      Object.keys(currentSectionSensors).forEach((sensorName) => {
        const wasEnabled = prevSectionSensors[sensorName] === true;
        const isNowEnabled = currentSectionSensors[sensorName] === true;

        // Only consider this a change if:
        // 1. The sensor was newly enabled (wasn't enabled before, is now)
        // 2. The sensor actually exists in this section's graph definition
        // STRICT VALIDATION: Only trigger fetch if sensor is explicitly in the legend
        const sensorExistsInSection = sectionSensorNames.includes(sensorName);

        if (!wasEnabled && isNowEnabled && sensorExistsInSection) {
          hasNewlyEnabledSensors = true;
          newlyEnabledSensors.push(sensorName);
        }
      });

      // Only trigger refetch if there are newly enabled sensors
      // Disabling sensors should NOT trigger a refetch
      if (hasNewlyEnabledSensors) {
        changedSections.add(sectionId);
        // Set loading state for all newly enabled sensors
        newlyEnabledSensors.forEach((sensorName) => {
          setSensorLoading(sectionId, sensorName, true);
        });
      }
    });

    // Check feature changes
    Object.keys({ ...prevFeatures, ...currentFeatures }).forEach((sectionId) => {
      const prevSectionFeatures = prevFeatures[sectionId] || {};
      const currentSectionFeatures = currentFeatures[sectionId] || {};

      if (JSON.stringify(prevSectionFeatures) !== JSON.stringify(currentSectionFeatures)) {
        changedSections.add(sectionId);
      }
    });

    // FIXED: Reload sections immediately without delay
    if (changedSections.size > 0) {
      // Use requestAnimationFrame to batch state updates
      requestAnimationFrame(() => {
        Array.from(changedSections).forEach((sectionId) => {
          fetchSectionData(sectionId, 'sensor-change');
        });
      });
    }
  }, [querySignature, graphDefinitions, resetTrigger, fetchSectionData, cancelAllRequests]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(abortControllersRef.current).forEach((controller) => {
        controller.abort();
      });
    };
  }, []);

  // Simple refetch function for external calls
  const refetchSectionData = useCallback(
    (sectionId: string, actionType: SensorActionType = 'refetch') => {
      return fetchSectionData(sectionId, actionType);
    },
    [fetchSectionData]
  );

  // Refetch specific sensor
  const refetchSensorData = useCallback(
    (sectionId: string, sensorName: string, actionType: SensorActionType = 'enable') => {
      return fetchSensorData_Individual(sectionId, sensorName, actionType);
    },
    [fetchSensorData_Individual]
  );

  // Force reload all data
  const forceReloadAllData = useCallback(() => {
    cancelAllRequests();

    // Don't clear data to avoid showing empty state
    // Just set loading state
    const loadingState: Record<string, boolean> = {};
    graphDefinitions.forEach((g: any) => {
      if (g.enabled !== false) {
        loadingState[g.id] = true;
      }
    });

    setState((prev) => ({
      ...prev,
      loading: loadingState,
      error: null,
    }));

    // Use requestAnimationFrame for consistent timing
    requestAnimationFrame(() => {
      const enabledSections = graphDefinitions.filter((g) => g.enabled !== false);
      enabledSections.forEach((graphDef) => {
        fetchSectionData(graphDef.id, 'force-reload');
      });
    });
  }, [cancelAllRequests, graphDefinitions, fetchSectionData]);

  // Individual section loading state checker
  const isSectionLoading = useCallback(
    (sectionId: string) => {
      return state.loading[sectionId] || false;
    },
    [state.loading]
  );

  // Check if a sensor has cached data (used to hide "no data" icon for previously focused sensors)
  const hasCachedDataForSensor = useCallback(
    (sectionId: string, sensorName: string) => {
      const cacheKey = `${sectionId}_${sensorName}`;
      return !!focusedSensorCacheRef.current[cacheKey];
    },
    []
  );

  return {
    // Data state
    data: state.data,
    error: state.error,

    // Loading state - both individual and global
    isSectionLoading,
    isAnySectionLoading,
    sectionLoadingStates: state.loading,
    isQueryParamsChanging: isAnySectionLoading,

    // Individual sensor loading state
    isSensorLoading,
    isAnySensorLoadingInSection,
    setSensorLoading,
    sensorLoadingStates: state.sensorLoading,

    // Cache check (for UI to know if sensor has cached data)
    hasCachedDataForSensor,

    // Actions
    refetchSectionData,
    refetchSensorData,
    forceReloadAllData,
    cancelSectionRequest,
    cancelAllRequests,

    // Legacy compatibility (simplified implementations)
    addSensorToData: refetchSectionData,

    // Additional data for compatibility
    sensors: [],
    realtimeData: {},
    lastUpdate: null,
    loadSensorData: useCallback(() => Promise.resolve(), []),
    startRealtimeUpdates: useCallback((_sensorIds: string[], _intervalMs?: number) => {
      // Legacy compatibility - do nothing
    }, []),
    stopRealtimeUpdates: useCallback(() => {
      // Legacy compatibility - do nothing
    }, []),
    refreshData: useCallback(() => {
      // Legacy compatibility - do nothing
    }, []),
    toggleSensorEnabled: useCallback((_sensorId: string) => {
      // Legacy compatibility - do nothing
    }, []),
    getSensorsByType: useCallback((_type: any) => [], []),
    getEnabledSensorsByType: useCallback((_type: any) => [], []),
  };
};
